import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CloudUpload, FolderOpen, InsertDriveFile } from '@mui/icons-material';
import UploadProgress from './UploadProgress';
import { UploadProgressState } from '../../types';

interface UploadDropzoneProps {
  isDragging: boolean;
  isUploading: boolean;
  remainingSlots: number;
  disabledReason?: string;
  onDragEnter: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onFolderSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  progress: UploadProgressState;
}

const DropzoneContainer = styled(Paper)<{ isDragging: boolean }>(({ theme, isDragging }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  borderWidth: '1px',
  borderStyle: 'dashed',
  borderColor: isDragging ? theme.palette.primary.main : theme.palette.grey[500],
  backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
  transition: 'border-color 0.3s, background-color 0.3s',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  elevation: 0,
}));

const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  isDragging,
  isUploading,
  remainingSlots,
  disabledReason,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onFolderSelect,
  onFileSelect,
  progress,
}) => {
  const disabled = isUploading || remainingSlots <= 0 || Boolean(disabledReason);
  const folderButtonLabel = disabledReason
    ? '確認中'
    : remainingSlots <= 0
      ? '上限に達しました'
      : 'フォルダを選択';
  const fileButtonLabel = disabledReason
    ? '確認中'
    : remainingSlots <= 0
      ? '上限に達しました'
      : 'ファイルを選択';

  const ignoreDisabledDragEvent = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <DropzoneContainer
      isDragging={isDragging}
      onDragEnter={disabled ? ignoreDisabledDragEvent : onDragEnter}
      onDragLeave={disabled ? ignoreDisabledDragEvent : onDragLeave}
      onDragOver={disabled ? ignoreDisabledDragEvent : onDragOver}
      onDrop={disabled ? ignoreDisabledDragEvent : onDrop}
    >
      <CloudUpload sx={{ fontSize: 60, mb: 2, color: 'grey.600' }} />
      <Typography sx={{ mb: 2 }}>
        ここにフォルダやファイルをドラッグ＆ドロップ
      </Typography>
      <Typography sx={{ mb: 2 }}>または</Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          component="label"
          disabled={disabled}
          startIcon={<FolderOpen />}
        >
          {folderButtonLabel}
          <input
            type="file"
            hidden
            webkitdirectory=""
            directory=""
            onChange={onFolderSelect}
            disabled={disabled}
          />
        </Button>
        <Button
          variant="contained"
          component="label"
          disabled={disabled}
          startIcon={<InsertDriveFile />}
        >
          {fileButtonLabel}
          <input
            type="file"
            hidden
            multiple
            onChange={onFileSelect}
            disabled={disabled}
          />
        </Button>
      </Box>
      {disabledReason && (
        <Alert severity="warning" sx={{ mt: 2, width: '100%' }}>
          {disabledReason}
        </Alert>
      )}
      {isUploading && <UploadProgress progress={progress} />}
    </DropzoneContainer>
  );
};

export default UploadDropzone;
